import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Columns3, List } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Skeleton,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table/interface';
import { Table } from 'antd';
import { ArrowRightOutlined, ReloadOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';

import { useTranslation } from '@govard/i18n-core/react';
import { useDataViewPagination } from '../../../shared/dataview/useDataViewPagination.js';
import { DuplicateContactError, useCreateContactMutation } from '../hooks/useContactMutations.js';
import { useContacts } from '../hooks/useContacts.js';

const { Text } = Typography;

interface CreateFormValues {
  firstName: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

interface ContactRow {
  id: string;
  fullName: string;
  email?: string | null;
  phone?: string | null;
  dealsCount: number;
  tags: string[];
}

type ContactListFilter = 'all' | 'with_deals' | 'with_email';

export function ContactsPage(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [form] = Form.useForm<CreateFormValues>();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const [duplicateId, setDuplicateId] = useState<string | null>(null);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [contactFilter, setContactFilter] = useState<ContactListFilter>('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const contactsQuery = useContacts(activeQuery ? { q: activeQuery } : {});
  const createMutation = useCreateContactMutation();

  useEffect(() => {
    if (!(createMutation.error instanceof DuplicateContactError)) {
      setDuplicateId(null);
      return;
    }
    setDuplicateId(createMutation.error.suggestion.id);
  }, [createMutation.error]);

  const duplicateSuggestion = useMemo(() => {
    if (!(createMutation.error instanceof DuplicateContactError)) return null;
    return createMutation.error.suggestion;
  }, [createMutation.error]);

  const summary = useMemo(() => {
    const contacts = contactsQuery.data ?? [];
    return {
      total: contacts.length,
      withDeals: contacts.filter((c) => c.dealsCount > 0).length,
      withEmail: contacts.filter((c) => Boolean(c.email)).length,
    };
  }, [contactsQuery.data]);

  const availableTags = useMemo(() => {
    const tags = new Set<string>();
    for (const contact of contactsQuery.data ?? []) {
      for (const tag of contact.tags) {
        tags.add(tag);
      }
    }
    return Array.from(tags).sort((a, b) => a.localeCompare(b));
  }, [contactsQuery.data]);

  const visibleContacts = useMemo(() => {
    return (contactsQuery.data ?? []).filter((contact) => {
      if (contactFilter === 'with_deals' && contact.dealsCount === 0) return false;
      if (contactFilter === 'with_email' && !contact.email) return false;
      if (tagFilter !== 'all' && !contact.tags.includes(tagFilter)) return false;
      return true;
    });
  }, [contactFilter, contactsQuery.data, tagFilter]);
  const pagination = useDataViewPagination(visibleContacts);

  async function handleCreate(values: CreateFormValues): Promise<void> {
    setDuplicateId(null);
    try {
      await createMutation.mutateAsync({
        firstName: values.firstName,
        ...(values.lastName ? { lastName: values.lastName } : {}),
        ...(values.email ? { email: values.email } : {}),
        ...(values.phone ? { phone: values.phone } : {}),
      });
      form.resetFields();
      setIsCreateOpen(false);
    } catch {
      // Error handled via createMutation.error
    }
  }

  function handleSearch(value: string): void {
    const nextQuery = value.trim();
    setSearchQuery(nextQuery);
    setActiveQuery(nextQuery);
    pagination.resetPage();
  }

  const columns: ColumnsType<ContactRow> = [
    {
      title: 'Контакт',
      dataIndex: 'fullName',
      key: 'fullName',
      render: (name: string, record) => (
        <Link
          id={`link-contact-${record.id}`}
          to={`/contacts/${record.id}`}
          style={{ fontWeight: 500, color: '#44403c' }}
        >
          {name}
        </Link>
      ),
    },
    {
      title: `${t('crm.contacts.form.email')} / ${t('crm.contacts.form.phone')}`,
      key: 'contact',
      render: (_: unknown, record) => (
        <Text type="secondary" style={{ fontSize: 13 }}>
          {record.email ?? record.phone ?? (
            <Text disabled style={{ fontSize: 13 }}>—</Text>
          )}
        </Text>
      ),
    },
    {
      title: 'Сделки',
      dataIndex: 'dealsCount',
      key: 'dealsCount',
      width: 100,
      align: 'center',
      render: (count: number) => (
        <Badge
          count={count}
          showZero
          style={{
            backgroundColor: count > 0 ? '#fffbeb' : '#f5f5f4',
            color: count > 0 ? '#d97706' : '#a8a29e',
            boxShadow: 'none',
            border: `1px solid ${count > 0 ? '#fbbf24' : '#e7e5e4'}`,
            fontFamily: 'monospace',
          }}
        />
      ),
    },
    {
      title: 'Теги',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags: string[]) => (
        <div className="flex flex-wrap gap-1">
          {tags.slice(0, 3).map((tag) => (
            <Tag key={tag} style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', lineHeight: '20px' }}>
              {tag}
            </Tag>
          ))}
          {tags.length > 3 && (
            <Tag style={{ fontSize: 10, color: '#a8a29e' }}>+{tags.length - 3}</Tag>
          )}
          {tags.length === 0 && <Text disabled style={{ fontSize: 12 }}>—</Text>}
        </div>
      ),
    },
    {
      title: 'Действие',
      key: 'action',
      align: 'center',
      render: (_: unknown, record) => (
        <Button
          size="small"
          icon={<ArrowRightOutlined />}
          onClick={(event) => {
            event.stopPropagation();
            navigate(`/contacts/${record.id}`);
          }}
        >
          Открыть
        </Button>
      ),
    },
  ];

  return (
    <div className="animate-fade-in flex h-full flex-col gap-6">
      <header className="z-10 flex w-full shrink-0 items-center justify-between">
        <div>
          <h1 className="m-0 mb-1 font-bold leading-tight tracking-tight" style={{ color: '#2d2418', fontSize: 28 }}>
            {t('crm.contacts.title')}
          </h1>
          <Text type="secondary" style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
            <span data-testid="contacts-visible-count">{visibleContacts.length} из {summary.total}</span> контактов · единая клиентская база
          </Text>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center rounded-lg p-1" style={{ background: '#e7e5e4', border: '1px solid #d6d3d1' }}>
            <Tooltip title="Канбан">
              <Button
                type="text"
                size="small"
                aria-label="Канбан"
                disabled
                style={{
                  minWidth: 32,
                  height: 28,
                  padding: 0,
                  color: '#a8a29e',
                  borderRadius: 6,
                }}
              >
                <Columns3 size={14} />
              </Button>
            </Tooltip>
            <Tooltip title="Список">
              <Button
                type="text"
                size="small"
                aria-label="Список"
                style={{
                  minWidth: 32,
                  height: 28,
                  padding: 0,
                  backgroundColor: '#fff',
                  color: '#d97706',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  borderRadius: 6,
                }}
              >
                <List size={14} />
              </Button>
            </Tooltip>
          </div>

          <Button
            type="primary"
            aria-label="Новый контакт"
            icon={<PlusOutlined />}
            onClick={() => {
              setDuplicateId(null);
              setIsCreateOpen(true);
            }}
            style={{ borderRadius: 8, height: 36, padding: '0 16px', fontWeight: 500 }}
          >
            Новый контакт
          </Button>
          <Tooltip title="Обновить">
            <Button
              aria-label="Обновить контакты"
              icon={<ReloadOutlined />}
              onClick={() => void contactsQuery.refetch()}
              style={{ borderRadius: 8, height: 36, width: 36 }}
            />
          </Tooltip>
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-6">
        <div
          className="flex w-full shrink-0 items-center gap-4 rounded-2xl border bg-white px-4 py-3 shadow-sm"
          style={{ borderColor: '#e7e5e4' }}
        >
          <div className="flex-1" style={{ maxWidth: 400 }}>
            <Input
              id="contacts-search"
              aria-label={t('crm.contacts.search_label')}
              placeholder="Поиск по контакту или телефону"
              value={searchQuery}
              onChange={(event) => {
                const nextQuery = event.target.value;
                setSearchQuery(nextQuery);
                setActiveQuery(nextQuery.trim());
                pagination.resetPage();
              }}
              onPressEnter={(event) => handleSearch(event.currentTarget.value)}
              variant="borderless"
              className="transition-colors"
              style={{ backgroundColor: '#f5f5f4', borderRadius: 8, padding: '8px 12px' }}
              allowClear
            />
          </div>

          <div className="ml-auto flex items-center gap-3">
            <select
              value={contactFilter}
              onChange={(event) => {
                setContactFilter(event.target.value as ContactListFilter);
                pagination.resetPage();
              }}
              aria-label="Фильтр контактов"
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">Все контакты</option>
              <option value="with_deals">Со сделками</option>
              <option value="with_email">С email</option>
            </select>

            <select
              value={tagFilter}
              onChange={(event) => {
                setTagFilter(event.target.value);
                pagination.resetPage();
              }}
              aria-label="Фильтр по тегу"
              className="bg-transparent font-medium transition-colors"
              style={{
                height: 36,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: '0 12px',
                color: '#44403c',
                fontSize: 14,
                outline: 'none',
                cursor: 'pointer',
              }}
            >
              <option value="all">Все теги</option>
              {availableTags.map((tag) => (
                <option key={tag} value={tag}>{tag}</option>
              ))}
            </select>
          </div>
        </div>

        <div
          className="min-h-0 flex-1"
          data-testid="contacts-list-view"
          style={{ background: '#fffdfa', border: '1px solid #e7e5e4', borderRadius: 8, overflow: 'hidden' }}
        >
          {contactsQuery.isLoading ? (
            <div className="flex flex-col gap-3 p-6">
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} avatar paragraph={{ rows: 1 }} active />
              ))}
            </div>
          ) : (
            <Table<ContactRow>
              dataSource={visibleContacts}
              columns={columns}
              rowKey="id"
              size="small"
              pagination={pagination.pagination}
              rowSelection={{
                selectedRowKeys: Array.from(selectedIds),
                onSelect: (record) => {
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    if (next.has(record.id)) {
                      next.delete(record.id);
                    } else {
                      next.add(record.id);
                    }
                    return next;
                  });
                },
                onSelectAll: (selected, __, changeRows) => {
                  setSelectedIds((current) => {
                    const next = new Set(current);
                    for (const record of changeRows) {
                      if (selected) {
                        next.add(record.id);
                      } else {
                        next.delete(record.id);
                      }
                    }
                    return next;
                  });
                },
              }}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={
                      <span style={{ color: '#a8a29e', fontSize: 13 }}>
                        Нет контактов по текущим фильтрам.
                      </span>
                    }
                  />
                ),
              }}
              onRow={(record) => ({
                'data-testid': `contact-row-${record.id}`,
                style: { cursor: 'pointer' },
                onClick: () => { navigate(`/contacts/${record.id}`); },
                onMouseEnter: (event) => {
                  event.currentTarget.style.backgroundColor = '#fffbeb';
                },
                onMouseLeave: (event) => {
                  event.currentTarget.style.backgroundColor = 'transparent';
                },
              })}
              style={{ fontSize: 13 }}
            />
          )}
        </div>
      </div>

      <Modal
        title={t('crm.contacts.create_title')}
        open={isCreateOpen}
        onCancel={() => {
          setIsCreateOpen(false);
          setDuplicateId(null);
        }}
        footer={null}
        width={440}
        destroyOnHidden={false}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values: CreateFormValues) => void handleCreate(values)}
          requiredMark={false}
          size="middle"
          className="mt-4"
        >
          <Form.Item
            name="firstName"
            label={<label htmlFor="contact-first-name" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>{t('crm.contacts.form.first_name')}</label>}
            rules={[{ required: true, message: 'Имя обязательно' }]}
          >
            <Input
              id="contact-first-name"
              aria-label={t('crm.contacts.form.first_name')}
              placeholder="Иван"
              prefix={<UserOutlined style={{ color: '#d6d3d1' }} />}
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={<label htmlFor="contact-last-name" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>{t('crm.contacts.form.last_name')}</label>}
          >
            <Input id="contact-last-name" aria-label={t('crm.contacts.form.last_name')} placeholder="Иванов" />
          </Form.Item>

          <Form.Item
            name="email"
            label={<label htmlFor="contact-email" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>{t('crm.contacts.form.email')}</label>}
            rules={[{ type: 'email', message: 'Некорректный email' }]}
          >
            <Input id="contact-email" aria-label={t('crm.contacts.form.email')} type="email" placeholder="ivan@example.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label={<label htmlFor="contact-phone" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600, color: '#78716c' }}>{t('crm.contacts.form.phone')}</label>}
          >
            <Input id="contact-phone" aria-label={t('crm.contacts.form.phone')} type="tel" placeholder="+7 (999) 000-00-00" />
          </Form.Item>

          {duplicateSuggestion ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, fontSize: 12 }}
              title={t('crm.contacts.duplicate_banner')}
              action={
                <Link
                  to={`/contacts/${duplicateId ?? duplicateSuggestion?.id}`}
                  aria-label={t('crm.contacts.open_contact')}
                  style={{ fontSize: 12, color: '#d97706', fontWeight: 600 }}
                >
                  → {t('crm.contacts.open_contact')}
                </Link>
              }
            />
          ) : null}

          <div className="flex justify-end gap-2">
            <Button onClick={() => setIsCreateOpen(false)}>{t('crm.common.cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              aria-label={t('crm.contacts.create_submit')}
              loading={createMutation.isPending}
              icon={<PlusOutlined />}
            >
              {t('crm.contacts.create_submit')}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}
